import { createAsyncThunk } from "@reduxjs/toolkit";

import { pollCart as pollAsyncCart } from "@app/modules/async-cart/store/thunks/poll-cart";
import { updatePaymentInfo } from "@app/modules/checkout/actions/update-payment";
import { setSelectedSeats } from "@app/modules/checkout/store/v2/slices/cart";
import type { CartPollResponse } from "@app/modules/checkout/types/cart-v2";
import { memoizedPassengersSeatsSelectedHasPrices } from "@app/modules/checkout/utils/bus";
import {
  getCartV2TripItem,
  isInstallmentsCharges
} from "@app/modules/checkout/utils/cart-v2";
import { getPaymentInfo } from "@app/modules/checkout/utils/form-fields/payment-info";
import {
  formatInstallments,
  getSpreedlySupportedCreditCards
} from "@app/modules/checkout/utils/payment-v2";
import { isAsyncCart } from "@app/modules/results/selectors/get-page";
import type { NapiClient } from "@app/services/api";
import type { TranslatorService } from "@app/services/translator";
import { closeModal } from "@app/store/state/modals";
import type { EmptyExternalInstallments } from "@app/types/checkout-types";
import type { CheckoutReduxStateV2 } from "@app/types/checkout-v2-types";
import type { NapiV2PassengerAddress, Purchaser } from "@app/types/napi-v2";
import type {
  NapiV2Cart,
  UpdateCartTripParams,
  UpdateCartTripPayload
} from "@app/types/napi-v2-cart-types";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";

import { applySeatSelectorPassengersOntoCartPassengers } from "./helpers/cart";

type UpdateCartTripThunkParams = {
  api: NapiClient;
  cart_id: string;
  trip_id: string;
  payload: UpdateCartTripPayload;
  params?: UpdateCartTripParams;
};

type updateCartTripSeatsSelectedParams = {
  api: NapiClient;
  trip_id: string;
  segment_id: string;
  seat_selector_passengers: PassengerSeatSelectionInstance[];
  translator: TranslatorService;
};

export const updateCartTripSeatsSelected = createAsyncThunk<
  void,
  updateCartTripSeatsSelectedParams,
  { state: CheckoutReduxStateV2 | ResultsReduxState }
>(
  "checkout/updateCartTripSeatsSelected",
  async (
    { api, trip_id, segment_id, seat_selector_passengers, translator },
    { getState, dispatch }
  ) => {
    const dispatchSelectedSeats = () =>
      dispatch(
        setSelectedSeats({
          segment_id,
          passengers: seat_selector_passengers
        })
      );

    const state = getState();
    // @ts-expect-error state have different shapes
    const cart_state = isAsyncCart(state)
      ? (state.cart as ResultsReduxState["cart"])
      : (state as CheckoutReduxStateV2);

    // @ts-expect-error state have different shapes
    const { cart } = isAsyncCart(state) ? cart_state : state;
    const trip_item = getCartV2TripItem(cart.items)!;

    const current_seat_ids = trip_item.passengers.map(
      passenger =>
        passenger.selected_seats?.find(seat => seat.segment_id === segment_id)
          ?.seat_id
    );

    const new_seat_ids = seat_selector_passengers.map(
      passenger =>
        passenger.selected_seats?.find(seat => seat.segment_id === segment_id)
          ?.seat_id
    );

    const vehicle = trip_item.segments.find(
      segment => segment.id === segment_id
    )?.vehicle!;

    const has_priced_seats = memoizedPassengersSeatsSelectedHasPrices({
      vehicle,
      new_seats_selected_id: new_seat_ids,
      previous_seats_selected_id: current_seat_ids
    });

    if (has_priced_seats) {
      dispatchSelectedSeats();

      // Cart can't be null here, this should never happen
      const cart = cart_state?.cart as NapiV2Cart;

      const cart_id = cart.id;
      const trip_item = getCartV2TripItem(cart.items)!;

      const passengers = trip_item.passengers.map((passenger, index) => {
        return applySeatSelectorPassengersOntoCartPassengers(
          passenger,
          seat_selector_passengers,
          index
        );
      });

      await dispatch(
        updateCartTrip({
          api,
          cart_id,
          trip_id,
          payload: {
            passengers,
            ticket_types: trip_item.ticket_types
          }
        })
      );

      // @ts-expect-error state have different shapes
      if (isAsyncCart(state)) {
        await pollAsyncCart(dispatch, api, cart_id);
      } else {
        await dispatch(pollCart({ api, cart_id, translator }));
      }
    } else {
      dispatchSelectedSeats();
    }

    dispatch(closeModal("all"));
  }
);

export const updateCartTrip = createAsyncThunk(
  "checkout/updateCartTripStatus",
  async ({
    api,
    cart_id,
    trip_id,
    payload,
    params
  }: UpdateCartTripThunkParams) => {
    const response = await api.updateCartTripV2(
      cart_id,
      trip_id,
      payload,
      params
    );
    return response;
  }
);

type PollCartParams = {
  api: NapiClient;
  cart_id: string;
  translator: TranslatorService;
};

const EMPTY_EXTERNAL_INSTALLMENTS: EmptyExternalInstallments = {
  type: "external_plans",
  quantity: 0,
  interest_rate: 0,
  plan_id: null,
  installment_amount: null,
  total_amount: null
};

export const pollCart = createAsyncThunk<
  CartPollResponse | undefined,
  PollCartParams,
  { state: CheckoutReduxStateV2 | ResultsReduxState }
>(
  "checkout/pollCart",
  async ({ api, cart_id, translator }, { getState, dispatch }) => {
    const response = await api.pollCartV2(cart_id).then(cart => {
      // Redeclare it here in case `updatePaymentInfo` has been called between the `fetchCart` call and its response.
      const state = getState();

      const installments_plans =
        "installments_plans" in state ? state.installments_plans : undefined;
      const prev_payment_info =
        "payment_info" in state ? state.payment_info : undefined;

      const installments = cart?.charges.items.find(isInstallmentsCharges);

      dispatch(
        updatePaymentInfo(
          getPaymentInfo({
            translator,
            payment_info: prev_payment_info,
            installments:
              installments && cart
                ? formatInstallments(installments, cart.charges.total)
                : !!installments_plans?.value?.length
                  ? EMPTY_EXTERNAL_INSTALLMENTS
                  : undefined,
            installments_plans: installments_plans?.value?.map(
              installments => ({
                ...installments,
                plan_id: installments.id,
                // TODO CART-V2 fix typing
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                installment_amount: installments.amount,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                total_amount: installments.amount
              })
            ),
            supported_credit_cards: getSpreedlySupportedCreditCards(
              cart?.payment_providers || []
            )
          })
        )
      );

      return cart;
    });
    return response;
  }
);

type UpdatePurchaserParams = {
  api: NapiClient;
  cart_id: string;
  purchaser: Purchaser;
};

export const updatePurchaser = createAsyncThunk(
  "checkout/updatePurchaserStatus",
  async ({ api, cart_id, purchaser }: UpdatePurchaserParams) => {
    const response = await api.updateCartPurchaser(cart_id, purchaser);
    return response;
  }
);

type UpdateBillingAddressParams = {
  api: NapiClient;
  cart_id: string;
  billing_address: NapiV2PassengerAddress;
};

export const updateBillingAddress = createAsyncThunk(
  "checkout/updateBillingAddress",
  async ({ api, cart_id, billing_address }: UpdateBillingAddressParams) => {
    const response = await api.updateCartBillingAddress(
      cart_id,
      billing_address
    );

    return response;
  }
);
